import { array, boolean, InferType, object, string } from "yup";
import { useMemo } from "react";
import InputField from "@/components/form/InputField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import CheckboxField from "@/components/form/CheckboxField";
import RlsUserGroupFields, {
  rlsUserGroupSchemaFields,
} from "./shared/RlsUserGroupFields";
import { useEntities } from "@/hooks/useCommonQueries";
import FilterConditionsField from "./shared/FilterConditionsField";
import { useFormikContext } from "formik";
import { Callout } from "@radix-ui/themes";
import { AlertTriangleIcon } from "lucide-react";
import PayloadFieldMapper from "./shared/PayloadFieldMapper";
import { WorkflowStep } from "@/types/breeze/Workflow";

export const updateDataValidationSchema = object({
  parameters: object({
    entity_name: string().required("Entity name is required"),
    payload: string().required("Payload is required"),
    ids: string(),
    filter_conditions: array().of(
      object({
        field: string().required("Field is required"),
        operator: string().required("Operator is required"),
        value: string().when("operator", {
          is: (op: string) => op !== "is_null" && op !== "is_not_null",
          then: (schema) => schema.required("Value is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    ),
    auto_set_rls: boolean(),
    rls_user_field: string(),
    auto_set_rls_groups: boolean(),
    rls_group_field: string(),
    ...rlsUserGroupSchemaFields,
    rls_mode: string(),
  }),
});

export type UpdateDataFormValues = InferType<typeof updateDataValidationSchema>;

interface UpdateDataFieldsProps {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function UpdateDataFields({ existingSteps = [], triggerEntity }: UpdateDataFieldsProps) {
  const { values } = useFormikContext<UpdateDataFormValues>();
  const { data: entities } = useEntities();
  const selectedEntityName = values?.parameters?.entity_name;

  // Initialize filter conditions if they don't exist
  if (!values.parameters.filter_conditions) {
    values.parameters.filter_conditions = [];
  }

  const entityOptions = useMemo(() =>
    entities?.map((e) => ({ value: e.name, label: e.name })) || [], [entities]);

  return (
    <div>
      <SearchableSelectField
        name="parameters.entity_name"
        label="Entity"
        required
        options={entityOptions}
        searchPlaceholder="Search entities..."
        placeholder="Select entity..."
      />

      <PayloadFieldMapper
        name="parameters.payload"
        entityName={selectedEntityName || ""}
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
        label="Payload"
        required
      />

      <InputField
        name="parameters.ids"
        label="Ids"
        description='Comma-separated record id(s) to update — supports templates like {{ $anythink.trigger.data.id }}. Takes precedence over filter conditions when set; refuses to run if both are empty.'
      />

      {(!values.parameters.ids || values.parameters.ids.trim() === "") &&
        values.parameters.filter_conditions &&
        values.parameters.filter_conditions.length === 0 && (
          <Callout.Root mb="4">
            <Callout.Icon>
              <AlertTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              No ids or filter conditions specified — the step will refuse to run.
            </Callout.Text>
          </Callout.Root>
        )}

      <FilterConditionsField
        name="parameters.filter_conditions"
        entityName={selectedEntityName}
      />

      <CheckboxField
        name="parameters.auto_set_rls"
        label="Auto-set RLS"
        description="Set row-level security access for the user specified in the update payload"
      />

      <SearchableSelectField
        name="parameters.rls_user_field"
        label="RLS User Field"
        options={[
          { value: "user", label: "user" },
          { value: "user_id", label: "user_id" },
          { value: "owner", label: "owner" },
          { value: "owner_id", label: "owner_id" },
          { value: "created_by", label: "created_by" },
        ]}
        variableToggle
        placeholder="user (default)"
        description="The field name in the payload that contains the user ID(s) for RLS"
      />

      <CheckboxField
        name="parameters.auto_set_rls_groups"
        label="Auto-set RLS Groups"
        description="Set row-level security access for the group specified in the update payload"
      />

      <SearchableSelectField
        name="parameters.rls_group_field"
        label="RLS Group Field"
        options={[
          { value: "group", label: "group" },
          { value: "group_id", label: "group_id" },
          { value: "team", label: "team" },
          { value: "team_id", label: "team_id" },
        ]}
        variableToggle
        placeholder="group (default)"
        description="The field name in the payload that contains the group ID(s) for RLS"
      />

      <SearchableSelectField
        name="parameters.rls_mode"
        label="RLS Mode"
        options={[
          { value: "add", label: "Add — grant access (keep existing)" },
          { value: "remove", label: "Remove — revoke access" },
          { value: "set", label: "Set — replace all access (transfer ownership)" },
        ]}
        placeholder="add (default)"
        description="How to apply RLS. Applies symmetrically to users and groups when both are set."
      />

      <RlsUserGroupFields />
    </div>
  );
}
